import { useRef, useEffect, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN

export default function VendorMap({
  schedules = [],
  center = [-122.4194, 37.7749],
  zoom = 12,
  onMarkerClick,
  userLocation,
  selectedScheduleId,
  hoveredScheduleId
}) {
  const mapContainer = useRef(null)
  const map = useRef(null)
  const markers = useRef([])
  const markerElements = useRef({})
  const userMarker = useRef(null)
  const [mapLoaded, setMapLoaded] = useState(false)

  // Initialize map
  useEffect(() => {
    if (map.current) return

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: center,
      zoom: zoom
    })

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right')

    map.current.on('load', () => {
      setMapLoaded(true)
    })

    return () => {
      map.current?.remove()
    }
  }, [])

  // Update markers when schedules change
  useEffect(() => {
    if (!mapLoaded) return

    // Clear existing markers
    markers.current.forEach(marker => marker.remove())
    markers.current = []
    markerElements.current = {}

    // Group schedules by location
    const locationMap = new Map()
    schedules.forEach(schedule => {
      if (!schedule.lat || !schedule.lng) return
      const key = `${schedule.lat.toFixed(5)},${schedule.lng.toFixed(5)}`
      if (!locationMap.has(key)) {
        locationMap.set(key, [])
      }
      locationMap.get(key).push(schedule)
    })

    // Create markers for each unique location
    locationMap.forEach((locationSchedules, key) => {
      const [lat, lng] = key.split(',').map(Number)
      const firstSchedule = locationSchedules[0]

      // Create popup content
      const popupContent = document.createElement('div')
      popupContent.className = 'popup-content'

      if (locationSchedules.length === 1) {
        popupContent.innerHTML = `
          <div class="popup-title">${firstSchedule.applicant}</div>
          <div class="popup-location">${firstSchedule.location_description || firstSchedule.address || ''}</div>
          <div class="popup-schedule"><strong>${firstSchedule.day_of_week}</strong> ${firstSchedule.start_24} - ${firstSchedule.end_24}</div>
        `
      } else {
        const vendorNames = [...new Set(locationSchedules.map(s => s.applicant))]
        const scheduleLines = locationSchedules
          .slice(0, 4)
          .map(s => `<strong>${s.day_of_week}</strong> ${s.start_24}-${s.end_24}`)
          .join('<br>')

        popupContent.innerHTML = `
          <div class="popup-title">${vendorNames.slice(0, 2).join(', ')}${vendorNames.length > 2 ? ` +${vendorNames.length - 2}` : ''}</div>
          <div class="popup-location">${firstSchedule.location_description || firstSchedule.address || ''}</div>
          <div class="popup-schedule">${scheduleLines}${locationSchedules.length > 4 ? '<br>...' : ''}</div>
        `
      }

      const popup = new mapboxgl.Popup({ offset: 25, closeButton: true })
        .setDOMContent(popupContent)

      // Create marker element
      const el = document.createElement('div')
      el.className = 'map-marker'

      const isSelected = locationSchedules.some(s => s.id === selectedScheduleId)
      const isHovered = locationSchedules.some(s => s.id === hoveredScheduleId)

      el.style.cssText = `
        width: ${isSelected || isHovered ? '36px' : '28px'};
        height: ${isSelected || isHovered ? '36px' : '28px'};
        background: ${isSelected ? '#006aff' : isHovered ? '#0051cc' : '#006aff'};
        border: 3px solid white;
        border-radius: 50%;
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 11px;
        font-weight: 700;
        transition: all 0.15s ease;
        transform: ${isSelected || isHovered ? 'scale(1.1)' : 'scale(1)'};
        z-index: ${isSelected ? 1000 : isHovered ? 999 : 1};
      `

      if (locationSchedules.length > 1) {
        el.textContent = locationSchedules.length
      }

      // Store reference for hover updates
      locationSchedules.forEach(s => {
        markerElements.current[s.id] = { el, locationSchedules }
      })

      const marker = new mapboxgl.Marker(el)
        .setLngLat([lng, lat])
        .setPopup(popup)
        .addTo(map.current)

      el.addEventListener('click', () => {
        if (onMarkerClick) {
          onMarkerClick(firstSchedule)
        }
      })

      markers.current.push(marker)
    })
  }, [schedules, mapLoaded])

  // Update marker styles on hover/select changes
  useEffect(() => {
    if (!mapLoaded) return

    Object.entries(markerElements.current).forEach(([id, { el, locationSchedules }]) => {
      const isSelected = locationSchedules.some(s => s.id === selectedScheduleId)
      const isHovered = locationSchedules.some(s => s.id === hoveredScheduleId)

      el.style.width = isSelected || isHovered ? '36px' : '28px'
      el.style.height = isSelected || isHovered ? '36px' : '28px'
      el.style.background = isSelected ? '#006aff' : isHovered ? '#0051cc' : '#006aff'
      el.style.transform = isSelected || isHovered ? 'scale(1.1)' : 'scale(1)'
      el.style.zIndex = isSelected ? 1000 : isHovered ? 999 : 1
    })
  }, [selectedScheduleId, hoveredScheduleId, mapLoaded])

  // Add user location marker
  useEffect(() => {
    if (!mapLoaded || !userLocation) return

    if (userMarker.current) {
      userMarker.current.remove()
    }

    const el = document.createElement('div')
    el.style.cssText = `
      width: 16px;
      height: 16px;
      background: #00a67e;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    `

    userMarker.current = new mapboxgl.Marker(el)
      .setLngLat([userLocation.lng, userLocation.lat])
      .addTo(map.current)

  }, [userLocation, mapLoaded])

  // Fly to selected location
  useEffect(() => {
    if (!mapLoaded || !selectedScheduleId) return

    const schedule = schedules.find(s => s.id === selectedScheduleId)
    if (schedule?.lat && schedule?.lng) {
      map.current.flyTo({
        center: [schedule.lng, schedule.lat],
        zoom: 15,
        duration: 800
      })
    }
  }, [selectedScheduleId, mapLoaded])

  return (
    <div
      ref={mapContainer}
      style={{ width: '100%', height: '100%' }}
    />
  )
}
